import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Plus, 
  Briefcase, 
  Zap, 
  Trash2,
  ChevronRight
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { AppProfileContext } from '@/contexts/AppProfileContext';
import { toast } from 'sonner';

const API_URL = window.__BACKEND_URL;

/**
 * App Profiles - Management Hub
 * 
 * PURPOSE: Central hub for managing App Profiles
 * LOCATION: Admin → App Settings → App Profiles
 * ACCESS: Admin-only
 * 
 * KEY RULES:
 * - No default App Profile exists
 * - App Profiles feed Self-Marketing Engine only
 * - Marketing identities for the Self-Marketing Engine
 */
export default function AppProfiles() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { appProfiles, activeProfileId, selectProfile, refreshProfiles, loading } = useContext(AppProfileContext);
  
  // Create dialog state
  const [showCreate, setShowCreate] = useState(false);
  const [createName, setCreateName] = useState('');
  const [createDescription, setCreateDescription] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Handle OAuth callback messages
  useEffect(() => {
    const success = searchParams.get('success');
    const error = searchParams.get('error');
    const platform = searchParams.get('platform');
    const ownerId = searchParams.get('owner_id');
    const dropbox = searchParams.get('dropbox');
    
    if (dropbox === 'connected') {
      toast.success('Dropbox connected successfully!');
      navigate('/app/admin/app-profiles', { replace: true });
    } else if (success === 'connected' && platform) {
      toast.success(`${platform.charAt(0).toUpperCase() + platform.slice(1)} connected successfully!`);
      // Navigate to the specific profile if owner_id provided
      if (ownerId) {
        navigate(`/app/admin/app-profile/${ownerId}`, { replace: true });
      } else {
        // Clear URL params
        navigate('/app/admin/app-profiles', { replace: true });
      }
    } else if (error) {
      toast.error(`Connection failed: ${decodeURIComponent(error)}`);
      navigate('/app/admin/app-profiles', { replace: true });
    }
  }, [searchParams, navigate]);

  const handleSelectProfile = (profileId) => {
    selectProfile(profileId);
    // Navigate to the unified App Profile page (no separate edit)
    navigate(`/app/admin/app-profile/${profileId}`);
  };

  const handleCreateProfile = async () => {
    if (!createName.trim()) {
      toast.error('Please enter a profile name');
      return;
    }
    
    setCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/app-profiles`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: createName.trim(),
          description: createDescription.trim()
        })
      });
      
      if (res.ok) {
        const newProfile = await res.json();
        toast.success('App Profile created');
        setShowCreate(false);
        setCreateName('');
        setCreateDescription('');
        refreshProfiles();
        // Navigate to new profile
        navigate(`/app/admin/app-profile/${newProfile.id}`);
      } else {
        toast.error('Failed to create App Profile');
      }
    } catch (err) {
      toast.error('Failed to create App Profile');
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteProfile = async (e, profileId) => {
    e.stopPropagation();
    if (!confirm('Are you sure you want to delete this App Profile?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/app-profiles/${profileId}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (res.ok) {
        toast.success('App Profile deleted');
        refreshProfiles();
      } else {
        toast.error('Failed to delete App Profile');
      }
    } catch (err) {
      toast.error('Failed to delete App Profile');
    }
  };
  
  // Filter out any legacy default profiles
  const realProfiles = appProfiles.filter(p => p.id !== 'default-app-profile');

  return (
    <div className="container mx-auto p-6 max-w-7xl" data-testid="app-profiles-page">
      {/* App Profile Selector (Always Visible) */}
      {realProfiles.length > 0 && (
        <div className="flex items-center gap-4 mb-6">
          <Label className="text-xs uppercase text-[#8A93A6] whitespace-nowrap">Active Profile</Label>
          <Select value={activeProfileId || ''} onValueChange={handleSelectProfile}>
            <SelectTrigger 
              className="bg-[#1C1F26] border-white/10 text-white w-64"
              data-testid="app-profile-selector-top"
            >
              <SelectValue placeholder="Select App Profile" />
            </SelectTrigger>
            <SelectContent>
              {realProfiles.map((profile) => (
                <SelectItem key={profile.id} value={profile.id}>
                  {profile.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {/* Page Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">App Profiles</h1>
          <p className="text-[#8A93A6]">Marketing identities for the Self-Marketing Engine</p>
        </div>
        <Button 
          onClick={() => setShowCreate(true)} 
          className="bg-cyan-500 hover:bg-cyan-600"
          data-testid="create-app-profile-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create App Profile
        </Button>
      </div>

      {/* Profiles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {realProfiles.map((profile) => (
          <Card
            key={profile.id}
            onClick={() => handleSelectProfile(profile.id)}
            className={`glass hover:bg-white/5 transition-all cursor-pointer ${
              profile.id === activeProfileId ? 'ring-2 ring-cyan-500/50' : ''
            }`}
            data-testid={`profile-card-${profile.id}`}
          >
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500 to-blue-500 flex items-center justify-center">
                  {profile.avatar_url ? (
                    <img src={profile.avatar_url} alt="" className="w-full h-full object-cover rounded-lg" />
                  ) : (
                    <Briefcase className="h-6 w-6 text-white" />
                  )}
                </div>
                <div className="flex-1">
                  <CardTitle className="text-base flex items-center gap-2">
                    {profile.name}
                    {profile.id === activeProfileId && (
                      <Badge variant="outline" className="border-cyan-500/30 text-cyan-400 text-xs">
                        Active
                      </Badge>
                    )}
                  </CardTitle>
                  <CardDescription className="text-xs mt-1 line-clamp-1">
                    {profile.description || 'No description'}
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <Badge variant="outline" className="border-pink-500/30 text-pink-400 text-xs">
                  <Zap className="h-3 w-3 mr-1" />
                  SME Ready
                </Badge>
                <div className="flex items-center gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => handleDeleteProfile(e, profile.id)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`delete-profile-${profile.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <ChevronRight className="h-4 w-4 text-[#8A93A6]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {realProfiles.length === 0 && !loading && (
          <Card className="glass col-span-full">
            <CardContent className="py-16 text-center">
              <Briefcase className="h-16 w-16 text-[#8A93A6] mx-auto mb-4" />
              <h3 className="text-white text-lg font-medium mb-2">No App Profiles</h3>
              <p className="text-[#8A93A6] mb-6">Create an App Profile to power the Self-Marketing Engine</p>
              <Button 
                onClick={() => setShowCreate(true)} 
                className="bg-cyan-500 hover:bg-cyan-600"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create First App Profile
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      
      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-[#1C1F26] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Create App Profile</DialogTitle>
            <DialogDescription>
              Marketing identity for the Self-Marketing Engine
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Profile Name</Label>
              <Input
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g., Consistency Marketing"
                className="bg-[#121417] border-white/10 text-white mt-2"
                data-testid="create-name-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                placeholder="Brief description of this marketing identity"
                className="bg-[#121417] border-white/10 text-white mt-2"
                rows={3}
                data-testid="create-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-white/10">
              Cancel
            </Button>
            <Button 
              onClick={handleCreateProfile} 
              disabled={creating}
              className="bg-cyan-500 hover:bg-cyan-600"
              data-testid="confirm-create-btn"
            >
              {creating ? 'Creating...' : 'Create Profile'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
